import React, { FC } from 'react';
import styled from '@emotion/styled';

import {
    BodyTextSmall,
    Button,
    ComponentTextSmall,
    ContentBanner,
    HeadingText40,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';

import { colors } from '@modernhealth/vital-foundation';
import familySvg from '../assets/marginalia-family-1.svg';
import { Trans, useTranslation } from 'react-i18next';
import { MHTermsLink } from 'src/modules/components/MHTermsLink';
import { useSelector } from 'react-redux';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';

interface WelcomeBoxProps {
    title: string;
}

const WelcomeBox: FC<WelcomeBoxProps> = ({ title, children }) => {
    return (
        <Box>
            <HeadingText40 marginBottom={31}>{title}</HeadingText40>
            {children}
        </Box>
    );
};

interface WelcomeScreenProps {
    onContinue: (actionName: string) => void;
    onContactUs: (buttonCopy: string) => void;
}

const ContactUs: FC<Pick<WelcomeScreenProps, 'onContactUs'>> = ({ onContactUs }) => {
    const { t } = useTranslation();

    return (
        <ContactUsButton onClick={() => onContactUs(t('dependents:common.contact_support'))}>
            <ComponentTextSmall underline fontColor="blue55">
                {t('dependents:common.contact_support')}
            </ComponentTextSmall>
        </ContactUsButton>
    );
};

export const WelcomeScreen: FC<WelcomeScreenProps> = ({ onContinue, onContactUs }) => {
    const { t } = useTranslation();
    const { coveredTherapyVisits } = useSelector(getSessionUtilizationInfo);
    const hasTherapySessions = coveredTherapyVisits > 0;

    const dependentConditions = [
        'welcome_screen.condition.1',
        hasTherapySessions ? 'welcome_screen.condition.2' : 'welcome_screen.condition.4',
        'welcome_screen.condition.3',
    ];
    return (
        <>
            <ContentBanner
                title={t('dependents:welcome_screen.title')}
                description={t('dependents:welcome_screen.description')}
                backgroundColor={colors.blue65}
                onClick={() => onContinue('Get started')}
                buttonTitle={t('dependents:welcome_screen.get_started')}
                image={familySvg}
                buttonProps={{
                    variant: 'secondary',
                }}
            />
            <ConditionsWrapper>
                <WelcomeBox title={t('dependents:welcome_screen.how_it_works.title')}>
                    <BodyTextSmall fontColor="gray75" css={{ whiteSpace: 'break-spaces' }}>
                        <Trans
                            t={t}
                            i18nKey={
                                hasTherapySessions
                                    ? 'dependents:welcome_screen.how_it_works.description'
                                    : 'dependents:welcome_screen.how_it_works.no_therapy.description'
                            }
                            components={{
                                strong: <b />,
                            }}
                        />
                    </BodyTextSmall>
                </WelcomeBox>
                <WelcomeBox title={t('dependents:welcome_screen.share.title')}>
                    <BodyTextSmall fontColor="gray75" marginBottom={23}>
                        {t('dependents:welcome_screen.share.description')}
                    </BodyTextSmall>
                    {dependentConditions.map((condition, index) => (
                        <ConditionContainer key={`condition_${index}`}>
                            <Icon name="checkmark-enclosed" size={22} color="cyan55" />
                            <BodyTextSmall marginLeft={12} fontColor="gray75">
                                {t(`dependents:${condition}`)}
                            </BodyTextSmall>
                        </ConditionContainer>
                    ))}
                    <Button
                        css={{ marginTop: pxToRem(21), zIndex: 10 }}
                        rounded
                        size="medium"
                        onClick={() => onContinue('Check eligibility')}
                    >
                        {t('dependents:common.check_eligibility')}
                    </Button>
                </WelcomeBox>
            </ConditionsWrapper>
            <FooterContainer>
                <FooterWrapper>
                    <BodyTextSmall marginTop={45}>
                        <Trans
                            t={t}
                            i18nKey="dependents:welcome_screen.extended.footer"
                            components={{
                                'mh-terms-link': <MHTermsLink />,
                                'contact-support-link': <ContactUs onContactUs={onContactUs} />,
                            }}
                        />
                    </BodyTextSmall>
                </FooterWrapper>
            </FooterContainer>
        </>
    );
};

const ConditionsWrapper = styled.div({
    marginTop: pxToRem(31),
    display: 'flex',
    flex: 1,
    gap: pxToRem(28),
});

const ConditionContainer = styled.div({
    marginBottom: pxToRem(14),
    display: 'flex',
    alignItems: 'center',
});

const Box = styled.div({
    width: '100%',
    border: `${pxToRem(2)} solid ${colors.gray05}`,
    borderRadius: pxToRem(6),
    padding: pxToRem(39, 40, 42),
    position: 'relative',
});

const ContactUsButton = styled.button({
    border: 'none',
    backgroundColor: 'unset',
    padding: 0,
    marginLeft: pxToRem(2),
});

const FooterContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const FooterWrapper = styled.div`
    max-width: 95%;
`;
